/**
 * MONSTER TOWN — VENUE + ECONOMY CREW ENTRY POINT.
 *
 * The one `install*(ctx)` line this crew owns in `src/main.ts`. Order inside
 * matters and is deliberate:
 *   1. venue definitions register (rooms and save sections need them),
 *   2. the service loop registers its save section,
 *   3. the economy registers its own section, its rooms and the shop,
 *   4. harness extras arm themselves for `app:booted`.
 * All of this happens BEFORE `save.load()` in main.ts, which is the only way a
 * save section is ever hydrated rather than silently defaulted.
 */

import './events'
import { bus } from '../core/events'
import { world, type SceneNode } from '../core/scene'
import type { Cell } from '../core/grid'
import type { SceneId } from '../core/types'
import type { GameContext } from '../app/context'
import { TOWN_SHOP_SCENE, VENUE_DEFS } from '../content/venue'
import { restaurant } from '../content/restaurant'
import { installEconomy } from '../economy/index'
import { installVenueHarness } from './harness'
import { updateWallCulling } from './scene'
import { installVenueService } from './service'
import { registerVenues, venueForScene } from './types'

/**
 * A ROOM IS FRAMED LIKE A ROOM, NOT LIKE A TOWN.
 *
 * The fixed `ZOOM_STEPS` are world-unit half-heights tuned outdoors, where a
 * tile is 2 units across. Our rooms are built on the interior grid, where a tile
 * is 1, so every step showed twice as much floor as it was meant to: the job —
 * three customers, their orders, the counter, the player — landed inside about a
 * quarter of the screen and a customer's face came out roughly fifty pixels
 * tall. Unreadable, and nothing a kid could do about it, because the widest step
 * was the default.
 *
 * So the room declares its own BOUNDS and the camera fits the ladder to them.
 * Step 2 is the whole room; we open on step 1 (0.78x of it), which is the frame
 * where you can still see every table AND read the face of the monster you are
 * serving — about four times the on-screen height it had before.
 *
 * Scoped deliberately: rooms this crew owns get bounds, the town gets them
 * cleared, and any OTHER crew's interior is left alone so two crews can never
 * fight over the same frame.
 */
const ROOM_ZOOM_INDEX = 1

function ownsRoom(sceneId: SceneId): boolean {
  return sceneId === TOWN_SHOP_SCENE || venueForScene(sceneId) !== undefined
}

/**
 * Point the zoom ladder at this scene. BOUNDS ONLY — it never touches which step
 * you are on, so a harness angle asking for step 0 keeps step 0 and a kid who
 * pinched keeps their pinch. Snapped, not lerped: you have just come through a
 * door behind a fade, and a 40-frame glide would be caught half-done by every
 * screenshot taken on arrival.
 */
function syncRoomBounds(ctx: GameContext, sceneId: SceneId): void {
  const node = world.get(sceneId)
  if (node === undefined) return
  if (!ownsRoom(sceneId)) {
    // Outdoors is the town ladder. Another crew's interior is their business.
    if (node.def.kind === 'exterior') ctx.camera.setFitBounds(null)
  } else {
    const g = node.grid
    ctx.camera.setFitBounds({
      w: g.spec.width * g.tileSize,
      d: g.spec.depth * g.tileSize,
      // Matches the wall height `buildRoomShell` raises, so a fitted frame never
      // slices the tops off the walls.
      h: g.levelHeight * 2.2,
      pad: 1.12,
    })
  }
  ctx.camera.setZoomIndex(ctx.camera.zoomLevel, true)
}

/**
 * PUT THE MONSTER DOWN WHERE THERE IS ROOM TO STAND.
 *
 * Walking in used to drop the kid on a fixed cell three tiles inside the door.
 * In the town shop that cell had a shelf on its shoulder, and under a fixed iso
 * camera a monster standing beside a shelf occupies the same patch of screen as
 * the thing ON the shelf — the captured frame read as the player clipping
 * through the goods.
 *
 * So: take the wanted cell if it is walkable AND has nothing directly beside it,
 * otherwise spiral outward for the nearest cell that is. Falls back to the
 * wanted cell rather than ever returning nothing — this can never be a dead end.
 */
function standingRoom(node: SceneNode, want: Cell): Cell {
  const g = node.grid
  const clear = (c: Cell): boolean => {
    if (!g.inBounds(c) || !g.isWalkable(c.gx, c.gz)) return false
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (dx === 0 && dz === 0) continue
        const n = { gx: c.gx + dx, gy: 0, gz: c.gz + dz }
        if (g.inBounds(n) && !g.isWalkable(n.gx, n.gz)) return false
      }
    }
    return true
  }
  if (clear(want)) return want
  for (let r = 1; r <= 4; r++) {
    for (let dx = -r; dx <= r; dx++) {
      for (let dz = -r; dz <= r; dz++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue
        const c = { gx: want.gx + dx, gy: 0, gz: want.gz + dz }
        if (clear(c)) return c
      }
    }
  }
  // Nowhere with elbow room: settle for anywhere walkable near the door.
  for (let r = 0; r <= 4; r++) {
    for (let dx = -r; dx <= r; dx++) {
      for (let dz = -r; dz <= r; dz++) {
        const c = { gx: want.gx + dx, gy: 0, gz: want.gz + dz }
        if (g.inBounds(c) && g.isWalkable(c.gx, c.gz)) return c
      }
    }
  }
  return want
}

export function installVenue(ctx: GameContext): void {
  // Venue #2 is registered exactly like venue #1: a data drop, nothing else.
  registerVenues([...VENUE_DEFS, restaurant])
  installVenueService(ctx)
  installEconomy(ctx)
  installVenueHarness()

  // Walking through a door drops you wherever you were standing outside, which
  // is roughly the doorway (the interiors' grid origins are chosen to line up).
  // Nudge the monster a couple of tiles further in so it never ends up stuck
  // half inside a wall.
  // Walking in for real opens on the working step, where the faces read.
  bus.on('scene:entered', (e) => {
    if (ownsRoom(e.scene)) {
      syncRoomBounds(ctx, e.scene)
      ctx.camera.setZoomIndex(ROOM_ZOOM_INDEX, true)
    }
    const node = world.get(e.scene)
    if (node === undefined || node.def.kind !== 'interior') return
    const def = venueForScene(e.scene)
    const door = def?.doorCell ?? { gx: Math.floor(node.grid.spec.width / 2), gy: 0, gz: node.grid.spec.depth - 1 }
    const inside = standingRoom(node, { gx: door.gx, gy: 0, gz: Math.max(0, door.gz - 3) })
    bus.emit('play:walkTo', { sceneId: e.scene as SceneId, target: node.grid.cellToWorld(inside) })
  })

  // Doll-house walls: hide the two nearest the camera, every frame. The same
  // pass re-fits the camera WHENEVER THE ACTIVE SCENE CHANGES, rather than on
  // `scene:entered` alone — the harness switches scenes with
  // `world.setActiveImmediate`, which fires no events at all, so an
  // event-only hook framed every captured room with whatever the town had left
  // behind. One id comparison per frame; it does nothing until the scene moves,
  // so a kid's own pinch is never yanked back.
  let framed: SceneId | null = null
  ctx.engine.addSystem({
    id: 'venue.walls',
    order: 51,
    render: () => {
      const active = world.activeSceneId
      if (active !== framed) {
        framed = active
        if (active !== null) syncRoomBounds(ctx, active)
      }
      updateWallCulling(ctx.camera)
    },
  })
}
